import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";

const EMPTY = {
  agencyName: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  fax: "",
  status: "Active",
};

const AgencyFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    if (record) {
      setForm({
        agencyName: record.agencyName || "",
        address: record.address || "",
        city: record.city || "",
        state: record.state || "",
        zip: record.zip || "",
        phone: record.phone || "",
        fax: record.fax || "",
        status: record.status || "Active",
      });
    } else {
      setForm(EMPTY);
    }
  }, [isOpen, record]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.agencyName.trim()) next.agencyName = "Agency name is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave?.({
        agencyName: form.agencyName.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        phone: form.phone.trim(),
        fax: form.fax.trim(),
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Agency Profile" : "Add Agency Profile"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}

      <LibraryField
        id="agency-name"
        label="Agency Name"
        required
        error={errors.agencyName}
      >
        <input
          id="agency-name"
          className={libraryInputClass(errors.agencyName)}
          value={form.agencyName}
          onChange={(e) => setField("agencyName", e.target.value)}
          maxLength={255}
          autoFocus
        />
      </LibraryField>

      <LibraryField id="agency-address" label="Address">
        <input
          id="agency-address"
          className={libraryInputClass()}
          value={form.address}
          onChange={(e) => setField("address", e.target.value)}
          maxLength={500}
        />
      </LibraryField>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <LibraryField id="agency-city" label="City">
          <input
            id="agency-city"
            className={libraryInputClass()}
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
            maxLength={100}
          />
        </LibraryField>
        <LibraryField id="agency-state" label="State">
          <input
            id="agency-state"
            className={libraryInputClass()}
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
            maxLength={50}
          />
        </LibraryField>
        <LibraryField id="agency-zip" label="Zip">
          <input
            id="agency-zip"
            className={libraryInputClass()}
            value={form.zip}
            onChange={(e) => setField("zip", e.target.value)}
            maxLength={20}
          />
        </LibraryField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="agency-phone" label="Phone">
          <input
            id="agency-phone"
            className={libraryInputClass()}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            maxLength={30}
          />
        </LibraryField>
        <LibraryField id="agency-fax" label="Fax">
          <input
            id="agency-fax"
            className={libraryInputClass()}
            value={form.fax}
            onChange={(e) => setField("fax", e.target.value)}
            maxLength={30}
          />
        </LibraryField>
      </div>

      <LibraryField id="agency-status" label="Status">
        <div className="relative">
          <select
            id="agency-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default AgencyFormModal;
